import { formatDate, formatTime } from './format';
import type { PdsOrder } from './types';

export interface StockPart {
  number: number;
  // Numéro complet de la commande, tel que le client le lit.
  ref: string;
  label: string;
  quantity: number;
}

export interface StockLine {
  id: string;
  quantity: number;
  name: string;
  option?: string;
  dueAt: string;
  // Sur plusieurs jours, la ligne dit aussi de quel jour elle est ; `dueTs` les range dans l'ordre du temps.
  dayLabel?: string;
  dueTs?: number;
  // Numéros des commandes qui demandent cette ligne, et parmi elles celles déjà en retard.
  orders: number[];
  lateOrders: number[];
  // Ce que chaque commande demande de cette ligne, pour répartir le lot.
  parts: StockPart[];
  done: boolean;
  // Pour une ligne à préparer : ce que le même produit, au même horaire, a déjà de préparé (cases cochées) dans d'autres
  // commandes. La carte le montre à côté du reste à faire, au lieu de le faire disparaître.
  // Pour chaque commande de la ligne (par son numéro) : la commande et les lignes de cette commande que la part couvre.
  // C'est ce qui permet de cocher une part depuis le volet latéral.
  sources?: Record<number, { orderId: string; itemIds: string[] }>;
  alreadyPrepared?: { quantity: number; parts: StockPart[] };
}

export interface StockZone {
  name: string;
  lines: StockLine[];
}

// Le serveur ne dit pas où un produit est rangé : tant que les zones ne sont pas déclarées, tout tient dans une seule.
export const DEFAULT_ZONE = 'Toutes les références';

// Une commande n'a rien à sortir si elle est annulée ou refusée.
const COUNTS_FOR_STOCK = (order: PdsOrder) =>
  order.stage !== 'cancelled' && order.stage !== 'refused';

// À préparer = une quantité pas encore cochée d'une commande pas encore remise ; préparé = cochée, ou commande remise.
export const compareLines = (a: StockLine, b: StockLine) =>
  a.dayLabel ? (a.dueTs ?? 0) - (b.dueTs ?? 0) : a.dueAt.localeCompare(b.dueAt);

export const buildStockZones = (
  orders: PdsOrder[],
  now: number,
  withDay = false,
): StockZone[] => {
  const lines = new Map<string, StockLine>();

  for (const order of orders.filter(COUNTS_FOR_STOCK)) {
    const dueAt = formatTime(order.dueAt);
    const dayLabel = withDay ? formatDate(order.dueAt) : undefined;

    for (const item of order.items) {
      // L'état se juge ligne par ligne : une quantité cochée quitte « à préparer » sans attendre la remise de la commande.
      // `done` fait partie de la clé de regroupement, donc un même produit au même horaire se scinde en deux lignes.
      const done = order.stage === 'handed' || item.prepared;
      const late = !done && order.dueAt < now;
      const id = [
        dayLabel ?? '',
        dueAt,
        item.name,
        item.option ?? '',
        done,
      ].join('|');
      const line = lines.get(id) ?? {
        id,
        quantity: 0,
        name: item.name,
        option: item.option,
        dueAt,
        dayLabel,
        dueTs: order.dueAt,
        orders: [],
        lateOrders: [],
        parts: [],
        done,
      };
      line.quantity += item.quantity;
      line.sources ??= {};
      (line.sources[order.number] ??= {
        orderId: order.id,
        itemIds: [],
      }).itemIds.push(item.id);
      const part = line.parts.find((p) => p.number === order.number);
      if (part) part.quantity += item.quantity;
      else
        line.parts.push({
          number: order.number,
          ref: String(order.orderNumber ?? order.number),
          label: order.label,
          quantity: item.quantity,
        });
      if (!line.orders.includes(order.number)) line.orders.push(order.number);
      if (late && !line.lateOrders.includes(order.number))
        line.lateOrders.push(order.number);
      lines.set(id, line);
    }
  }

  // La clé finit par l'état (`|false` / `|true`) : la ligne préparée jumelle de chaque ligne à préparer se retrouve en
  // changeant cette fin.
  for (const line of lines.values()) {
    if (line.done) continue;
    const twin = lines.get(line.id.replace(/\|false$/, '|true'));
    if (twin)
      line.alreadyPrepared = { quantity: twin.quantity, parts: twin.parts };
  }

  const sorted = [...lines.values()].sort(compareLines);
  return sorted.length ? [{ name: DEFAULT_ZONE, lines: sorted }] : [];
};
